import type { ReactNode } from 'react';
import { QueueFlowContext } from './flowContext';
import { useQueueActions } from './useQueueActions';
import styles from './queue.module.css';

/**
 * One queue flow for the whole app.
 *
 * Every video card in every grid can add to the queue and can be pressed, so the naive shape — the
 * hook inside the card — would mount forty copies of it per page and let two of them be answering at
 * once. What is actually per-card is only which video was pressed, and that already travels as an
 * argument. So the flow is mounted **once**, here, inside `PlaybackHost` because building a queue
 * around the video already playing needs the session it provides.
 *
 * One mount is also what makes "only one card is ever asking" structurally true rather than a rule
 * every grid has to remember: the pressed card is a single value in this hook's state, and a card
 * draws the two answers over its picture exactly when it is that value (`QueueCardAsk.tsx`).
 */
export function QueueActionsHost({ children }: { readonly children: ReactNode }) {
  const actions = useQueueActions();
  return (
    <QueueFlowContext.Provider value={actions}>
      {children}
      {/*
       * What the last press did, spoken and shown. Three of the five outcomes are invisible on the
       * page the reader is standing on — the queue changed somewhere else — so without this an
       * "Add to queue" press looks like a button that did nothing.
       */}
      <span className={styles.outcome} role="status">
        {actions.feedback}
      </span>
    </QueueFlowContext.Provider>
  );
}
